import { Alert, Flex, Radio, Typography } from 'antd';
import Linkify from 'linkify-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import SchemaForm from '@/components/form/SchemaForm/SchemaForm';
import { WAITING_CLIENT, WAITING_RECRUITER } from '@/constants/statuses';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LabelTitle, SectionHeading } from '@/UI/typography';
import { formatLongDate, formatTime } from '@/utils/formatDate';
import { buildProposeDateFormSchema } from '@/utils/validation/formSchemas/jobSchemas';

import type { FormSize } from '@/components/form/SchemaForm/sizing';
import type { SchemaSection } from '@/components/form/SchemaForm/types';
import type { InterviewRoom, ProposedDate } from '@/types/interview';

interface ConfirmProps {
  dates: ProposedDate[];
  interview: InterviewRoom;
  loading: boolean;
  onConfirm: (id: string) => void;
  size?: FormSize;
}

const ConfirmProposeDate = ({
  dates,
  onConfirm,
  interview,
  loading,
  size,
}: ConfirmProps) => {
  const { t, i18n } = useTranslation();
  const {
    user,
    isClient: isCurrentUserClient,
    isRecruiter: isCurrentUserRecruiter,
    isAdmin,
  } = useCurrentUser();

  const planningTask = interview.current_stage?.tasks?.find(
    (task) => task.is_planning_task,
  );

  const waitingForRecruiter =
    interview.current_stage?.status === WAITING_RECRUITER;

  const waitingForClient = interview.current_stage?.status === WAITING_CLIENT;

  const isClient = isCurrentUserClient || planningTask?.email === user?.email;

  const isRecruiter =
    isCurrentUserRecruiter && planningTask?.email !== user?.email;

  const submitDisabled =
    (isClient && waitingForRecruiter) || (isRecruiter && waitingForClient);

  const formSchema = useMemo(() => buildProposeDateFormSchema(t), [t]);

  const sections = useMemo<SchemaSection[]>(
    () => [
      {
        key: 'propose-date',
        fields: [
          {
            name: 'date',
            type: 'custom',
            render: () => (
              <Radio.Group name="propose_date">
                {dates.map((date) => (
                  <Radio
                    value={date.id}
                    key={date.id}
                    disabled={
                      (isClient && waitingForRecruiter) ||
                      (isRecruiter && waitingForClient)
                    }
                  >
                    <Flex>
                      <SectionHeading>
                        {formatLongDate(date.time_from, i18n.language)},{' '}
                        {formatTime(date.time_from, i18n.language)} -{' '}
                        {formatTime(date.time_to, i18n.language)}
                      </SectionHeading>
                      <Typography.Text type="secondary">
                        {date.place}
                      </Typography.Text>
                    </Flex>
                  </Radio>
                ))}
              </Radio.Group>
            ),
          },
        ],
      },
      {
        key: 'comments',
        title: t('COMMENTS'),
        fields: [
          {
            key: 'comment-body',
            type: 'custom',
            render: () => <Linkify tagName="p">{dates[0]?.comment}</Linkify>,
          },
        ],
      },
    ],
    [
      t,
      dates,
      isClient,
      isRecruiter,
      waitingForRecruiter,
      waitingForClient,
      i18n.language,
    ],
  );

  const onFinish = (values: { date?: string }) => {
    if (values.date) {
      onConfirm(values.date);
    }
  };

  return dates.length ? (
    <SchemaForm
      scrollToFirstError
      size={size}
      formSchema={formSchema}
      onFinish={onFinish}
      sections={sections}
      footer={
        !isAdmin
          ? {
              actions: [
                {
                  label: t('CONFIRM_DATE'),
                  htmlType: 'submit',
                  type: 'primary',
                  disabled: submitDisabled,
                },
              ],
            }
          : undefined
      }
    >
      {isClient && waitingForRecruiter ? (
        <Alert type="success" showIcon={false} title={t('WAITING_RECRUITER')} />
      ) : isRecruiter && waitingForClient ? (
        <Alert type="success" showIcon={false} title={t('WAITING_CLIENT')} />
      ) : null}
    </SchemaForm>
  ) : !loading ? (
    <LabelTitle>{t('NO_RESULT')}</LabelTitle>
  ) : (
    <></>
  );
};

export default ConfirmProposeDate;
